import { useCallback, useState } from 'react';

import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { roomAtom } from '../../video-sdk';
import { useCompleteSessionCallback } from '../complete-session/useCompleteSessionCallback';
import { useAdmitClientCallback } from './useAdmitClientCallback';

export type StartNewClientSessionCallback = (
  visitorUuid: string,
  visitorName: string,
  patientShortId: string,
) => Promise<boolean | undefined>;

export function useStartNewClientSessionCallback(): [StartNewClientSessionCallback, { loading?: boolean }] {
  const [awaitingCallbacks, setAwaitingCallbacks] = useState(false);
  const completeSession = useCompleteSessionCallback();
  const [admitClient, { loading: waitingForParticipantToJoin }] = useAdmitClientCallback();
  const getRoom = useAtomCallbackMemoized((get) => get(roomAtom), []);
  const loading = awaitingCallbacks || waitingForParticipantToJoin;
  const callback = useCallback(
    async (visitorUuid: string, visitorName: string, patientShortId: string) => {
      const room = getRoom();

      if (!room) return;
      setAwaitingCallbacks(true);
      // normally we can skip awaiting session completion, but we must await
      // here since we're starting a new session immediately afterwards
      await completeSession();
      const status = !!(await admitClient({
        visitorId: visitorUuid,
        visitorName,
        patientShortId,
      }));
      setAwaitingCallbacks(false);
      return status;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return [callback, { loading }];
}
